// Full view of one woven social post: the complete body, the first
// comment, copy buttons for both, and mark-posted. Opened from This Week
// on the dashboard and from the Social schedule, so posting day is
// copy-copy-done without leaving the screen you are on.

import { useState } from 'react';
import { apiFetch } from '../../../utils/apiFetch';
import { useMarketing } from '../MarketingContext';
import { parseLocalDate } from '../dates';
import { Pill } from '../ui';

export default function SocialPostModal({ post, onClose }) {
  const { reloadSocial } = useMarketing();
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);

  if (!post) return null;

  async function markPosted() {
    setBusy(true);
    setErr(null);
    try {
      const res = await apiFetch(`/api/data/social/posts/${post.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ status: 'published' }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || `HTTP ${res.status}`);
      await reloadSocial();
      onClose();
    } catch (e) {
      setErr(e.message);
    } finally {
      setBusy(false);
    }
  }

  const when = post.scheduledDate
    ? parseLocalDate(post.scheduledDate).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })
    : 'unscheduled';

  return (
    <div className="fixed inset-0 z-50 bg-navy/20 grid place-items-center p-4" onClick={onClose}>
      <div
        className="bg-white border border-line rounded-[10px] shadow-[0_8px_24px_rgba(2,10,40,0.10)] w-[560px] max-w-full max-h-[85vh] flex flex-col"
        onClick={e => e.stopPropagation()}
      >
        <div className="flex items-center justify-between gap-3 px-4 py-3 border-b border-line">
          <div className="min-w-0">
            <div className="text-deep-teal text-[10.5px] font-black tracking-[0.1em] uppercase">
              {post.seriesName || 'Social post'}
            </div>
            <div className="text-navy text-[13.5px] font-black mt-0.5">
              {when}
              {post.pinned && <Pill tone="neutral" className="ml-2 text-[9px]">pinned</Pill>}
              {post.status === 'published' && <Pill tone="good" className="ml-2 text-[9px]">posted{post.publishedDate ? ` ${String(post.publishedDate).slice(0, 10)}` : ''}</Pill>}
            </div>
          </div>
          <button onClick={onClose} className="text-ink-3 hover:text-navy text-[16px] font-black px-1">✕</button>
        </div>

        <div className="overflow-y-auto px-4 py-3">
          <div className="flex items-center justify-between mb-1.5">
            <span className="text-[10px] font-black uppercase tracking-[0.08em] text-ink-3">Post</span>
            <CopyBtn text={post.body} label="Copy post" />
          </div>
          <div className="text-[13px] text-ink whitespace-pre-wrap leading-relaxed bg-[#faf9f6] border border-line-2 rounded-[7px] px-3 py-2.5">
            {post.body}
          </div>

          {post.firstComment && (
            <>
              <div className="flex items-center justify-between mt-3.5 mb-1.5">
                <span className="text-[10px] font-black uppercase tracking-[0.08em] text-ink-3">First comment</span>
                <CopyBtn text={post.firstComment} label="Copy comment" />
              </div>
              <div className="text-[13px] text-ink whitespace-pre-wrap leading-relaxed bg-[#faf9f6] border border-line-2 rounded-[7px] px-3 py-2.5">
                {post.firstComment}
              </div>
            </>
          )}
        </div>

        <div className="flex items-center justify-between gap-3 px-4 py-3 border-t border-line">
          <span className="text-[11px] text-rose">{err || ''}</span>
          {post.status !== 'published' ? (
            <button
              onClick={markPosted}
              disabled={busy}
              className="bg-deep-teal text-white text-[12px] font-black rounded-[6px] px-4 py-1.5 disabled:opacity-50"
            >
              {busy ? 'Marking…' : 'Mark posted ✓'}
            </button>
          ) : (
            <span className="text-[12px] text-ink-2">Already posted.</span>
          )}
        </div>
      </div>
    </div>
  );
}

function CopyBtn({ text, label }) {
  const [copied, setCopied] = useState(false);
  return (
    <button
      onClick={async () => {
        try {
          await navigator.clipboard.writeText(text);
          setCopied(true);
          setTimeout(() => setCopied(false), 1500);
        } catch { /* clipboard unavailable */ }
      }}
      className={`text-[11px] font-black rounded-[6px] px-2.5 py-1 border transition-colors ${
        copied
          ? 'bg-deep-teal text-white border-deep-teal'
          : 'text-bullogic-blue border-bullogic-blue/40 hover:bg-bullogic-blue/5'
      }`}
    >
      {copied ? 'Copied ✓' : label}
    </button>
  );
}
